import { Box, useMediaQuery } from '@mui/material'
import { DATA_FILE_NAME, DATA_FILE_PATH, VARIABLE_LIST } from 'constant'
import { uniqueId } from 'lodash'
import { FC, Fragment, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useDataWithMetric } from 'utils'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import { YearSlider } from 'components/common/RangeSlider/YearSlider'
import { H3 } from '../MethodologyAndMetrica.styled'
import { ColorBox, CountryContainer, CountryName, CountryTH, IndicatorTD } from './CoverageScorecard.styled'
import { ValueRange, ValueRangeValue } from './CoverageChart.styled'

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 800,
    fontSize: '14px',
    border: '1px solid #000',
    fontFamily: 'CircularStdBook, sans-serif',
    // whiteSpace: 'nowrap',
    // width: 'fit-content',
  },
}))

const TableWrapper: FC<PropsWithChildren> = ({ children }) => (
  <Box display={'flex'} flexDirection={'row'} justifyContent={'center'} sx={{ overflowX: 'auto' }}>
    {children}
  </Box>
)

const CoverageScorecard = () => {
  const csvFullData = useDataWithMetric(`${DATA_FILE_PATH}${DATA_FILE_NAME}.csv`)
  const variableList = useDataWithMetric(`${DATA_FILE_PATH}${VARIABLE_LIST}.csv`)

  const [activeCountry, setActiveCountry] = useState<string>('')
  const [currentYear, setCurrentYear] = useState(2023)

  const isSmallerThan1520 = useMediaQuery('(max-width:1536px)')

  const indicatorsList = useMemo(() => {
    const list =
      variableList
        ?.filter((ind: any) => ind?.display_name && ind?.display_data_source && ind?.pillar && ind?.pillar_color_code)
        ?.map((ind: any) => ({
          ...ind,
          norm_column_name: `${ind?.column_name}`,
          // norm_column_name: `norm_${ind?.column_name}_display`,
          indicator: ind.display_name,
        }))
        ?.sort((a: any, b: any) => a?.indicator?.localeCompare(b?.indicator)) || []
    return list
  }, [variableList])
  const countryList = useMemo(() => {
    const list = csvFullData
      ?.filter((row: any) => {
        return row?.PublishYear === String(currentYear)
      })
      ?.map((row: any) => ({
        titleText: row.CountryName,
        title: row.CountryName,
        key: row?.Code,
        code: row?.Code,
      }))
      ?.sort((a: any, b: any) => a?.titleText?.localeCompare(b?.titleText))

    return list || []
  }, [csvFullData, currentYear])
  const dataByYear = useMemo(() => {
    const filterByYear = csvFullData
      ?.filter((row: any) => {
        return row?.PublishYear === String(currentYear)
      })
      ?.sort((a: any, b: any) => a?.CountryName?.localeCompare(b?.CountryName))
    const list: any[] = []
    const scoreByCountry: any = {}
    indicatorsList?.forEach((ind: any) => {
      const indicatorItem: any = {
        indicator: ind.indicator,
        tooltipTitle: `${ind?.indicator}:`,
      }
      const columnName = ind.norm_column_name
      let countriesWithData = 0
      filterByYear?.forEach((countryData: any) => {
        const code = countryData.Code
        const noData = isNaN(parseFloat(countryData[columnName]))
        indicatorItem[code] = noData ? '0' : '1'
        countriesWithData += noData ? 0 : 1
        if (!scoreByCountry.hasOwnProperty(code)) {
          scoreByCountry[code] = noData ? 0 : 1
        } else {
          scoreByCountry[code] += noData ? 0 : 1
        }
      })
      indicatorItem.tooltipContent = `${countriesWithData} out of ${filterByYear?.length} countries`
      list.push(indicatorItem)
    })

    return { list, scoreByCountry }
  }, [csvFullData, indicatorsList, currentYear, countryList])

  const getCountryTooltip = useCallback(
    (c: any) => {
      return `${c?.title}: ${dataByYear?.scoreByCountry?.[c?.code]} out of ${dataByYear?.list?.length}`
    },
    [dataByYear],
  )

  return (
    <Fragment>
      <Box
        width={'100%'}
        display='flex'
        alignItems={'center'}
        flexDirection={'column'}
        maxWidth={1550}
        justifyContent={'center'}
        margin={'auto'}
      >
        <Box width={'100%'} maxWidth={1624} alignSelf={'center'}>
          <Box display={'flex'} flexDirection={{ md: 'row', xs: 'column' }}>
            <H3 style={{ display: 'block', flex: 2 }}>{currentYear} Data Coverage Scorecard by Country</H3>
            <Box
              flex={1}
              margin={'0 10px'}
              marginTop={{ md: 0, xs: '50px' }}
              display={'flex'}
              alignItems={'center'}
              position={'relative'}
              justifyContent={'center'}
            >
              <span style={{ position: 'absolute', bottom: '50px' }}>Select Year</span>
              <YearSlider value={currentYear} onChange={setCurrentYear} />
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} gap={'24px'} marginTop={'20px'}>
            <ValueRange $bg={'#E7E7E7'}>
              <ValueRangeValue>{'No data'}</ValueRangeValue>
            </ValueRange>
            <ValueRange $bg={'#428BC5'}>
              <ValueRangeValue>{'Data available'}</ValueRangeValue>
            </ValueRange>
          </Box>
        </Box>
      </Box>
      <Box
        paddingBottom={5}
        sx={{ overflowX: 'auto' }}
        maxWidth={1550}
        margin={'auto'}
        display={'flex'}
        flexDirection={'column'}
        alignItems={isSmallerThan1520 ? 'start' : 'center'}
      >
        <Box height={'120px'}></Box>
        <table
          style={{ borderCollapse: 'collapse' }}
          onMouseLeave={() => {
            setActiveCountry('')
          }}
        >
          <tr>
            <th />
            {countryList.map((c: any) => (
              <CountryTH key={c?.title}>
                <CountryContainer>
                  <HtmlTooltip title={getCountryTooltip(c)} followCursor>
                    <CountryName
                      onMouseEnter={() => {
                        setActiveCountry(c?.key)
                      }}
                      onMouseLeave={() => {
                        setActiveCountry('')
                      }}
                      $active={activeCountry === c?.key}
                      style={{
                        fontFamily: 'CircularStdBook',
                        fontSize: '14px',
                      }}
                    >
                      {c.title}
                    </CountryName>
                  </HtmlTooltip>
                </CountryContainer>
              </CountryTH>
            ))}
          </tr>
          <tbody>
            {dataByYear.list?.map((row: any) =>
              row?.indicator ? (
                <tr key={row?.indicator}>
                  <HtmlTooltip title={`${row.tooltipTitle} ${row.tooltipContent}`} followCursor>
                    <IndicatorTD
                      onMouseEnter={() => {
                        setActiveCountry('')
                      }}
                    >
                      {row?.indicator}
                    </IndicatorTD>
                  </HtmlTooltip>
                  {countryList?.map((c: any) => (
                    <Fragment key={uniqueId('td_')}>
                      <HtmlTooltip title={getCountryTooltip(c)} followCursor>
                        <td
                          data-tooltip-id='color-box-tooltip-id'
                          data-tooltip-content={getCountryTooltip(c)}
                          style={{
                            padding: '3px 2px',
                            cursor: 'pointer',
                            width: '25px',
                          }}
                          onMouseEnter={() => {
                            setActiveCountry(c?.key)
                          }}
                          onMouseLeave={() => {
                            setActiveCountry('')
                          }}
                        >
                          <ColorBox
                            $bg={row?.[c?.code] === '0' ? '#E7E7E7' : activeCountry === c?.key ? '#55AFE5' : '#428BC5'}
                            $active={activeCountry === c?.key}
                          />
                        </td>
                      </HtmlTooltip>
                    </Fragment>
                  ))}
                </tr>
              ) : null,
            )}
          </tbody>
        </table>
      </Box>
    </Fragment>
  )
}

export default CoverageScorecard
